import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  type ForwardedRef,
} from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@wartek-id/button-v1'
import { Separator } from '@wartek-id/separator'
import { Text } from '@wartek-id/text'

import useContentContext from 'app/KinerjaKSPage/hooks/useContentContext'
import useSKPKSTrackEvent from 'app/KinerjaKSPage/hooks/useSKPKSTrackEvent'
import KinerjaDropdownGroupSingle from 'app/KinerjaKSPage/components/KinerjaDropdown/KinerjaDropdownGroupSingle'

import type { KinerjaSelection, SKPTabHandler } from 'types/kinerja'
import type {
  KinerjaKsPerencanaanTabBaseProps,
  UpdateSKPKSPayloadWithoutNpsn,
} from 'types/kinerja-ks'

import ImgDocument from 'assets/icons/img-document.svg'

export const MIN_POINT = 32

type Props = KinerjaKsPerencanaanTabBaseProps & {
  customStickyFooter?: () => JSX.Element
  onChange?: (values: KinerjaSelection[]) => void
}

const filterEmptyValues = (value: KinerjaSelection) => value.id !== 0

const Tab2Year2025Onwards = (
  {
    skp,
    isSubmitting = false,
    onClickNext = () => {},
    onClickSave = () => {},
    onChange = () => {},
    customStickyFooter,
  }: Props,
  ref: ForwardedRef<SKPTabHandler>
) => {
  const { trackClickDraftPlanning, trackClickToPerilakuKerja } =
    useSKPKSTrackEvent()

  const { contentPengembanganKompetensi } = useContentContext()

  const { setValue, getValues, handleSubmit, watch } = useForm<{
    pengembanganKompetensi: KinerjaSelection[]
  }>({
    defaultValues: {
      pengembanganKompetensi: skp?.pengembanganKompetensi ?? [],
    },
  })

  const selectedValues = watch('pengembanganKompetensi')
  const isDisabledSubmitButton = selectedValues.length === 0

  const handleChange = (values: KinerjaSelection[]) => {
    const filteredValues = values.filter(filterEmptyValues)
    setValue('pengembanganKompetensi', filteredValues)

    onChange(filteredValues)
  }

  // Field `npsn` can be safely ignored since it will be automatically added to the payload.
  const generatePayload = useCallback((): UpdateSKPKSPayloadWithoutNpsn => {
    return {
      pengembanganKompetensi: getValues('pengembanganKompetensi').filter(
        (k) => k.id > 0
      ), // Only include the valid ones,
    }
  }, [getValues])

  const onSubmit = () => {
    submitForm()
  }

  const submitForm = () => {
    trackClickToPerilakuKerja({
      kinerjaPlanningId: skp.id,
      kinerjaPlanningStatus: skp.status,
      screenName: 'KINERJA_PLANNING_KS_PENGEMBANGAN_KOMPETENSI_PAGE',
    })

    onClickNext(generatePayload())
  }

  const onSaveDraft = useCallback(() => {
    trackClickDraftPlanning({
      kinerjaPlanningId: skp.id,
      kinerjaPlanningStatus: skp.status,
      screenName: 'KINERJA_PLANNING_KS_PENGEMBANGAN_KOMPETENSI_PAGE',
    })

    onClickSave(generatePayload())
  }, [onClickSave, generatePayload, trackClickDraftPlanning, skp])

  useImperativeHandle(
    ref,
    () => ({
      saveDraft: onSaveDraft,
    }),
    [onSaveDraft]
  )

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-9">
          <div className="flex gap-3">
            <ImgDocument className="w-16 h-16 shrink-0" />
            <div className="space-y-1">
              <Text variant="heading-md">
                Pilih 1 Indikator Kompetensi untuk menjadi fokus peningkatan
                kinerja Anda
              </Text>
              <Text
                as="ul"
                className="list-disc list-outside ml-4"
                color="subdued"
              >
                <Text as="li" color="subdued">
                  Diskusi bersama Tim Kinerja terkait kegiatan yang dapat
                  dilakukan untuk meningkatkan kompetensi berdasarkan indikator
                  yang dipilih.
                </Text>
                <Text as="li" color="subdued">
                  Di tahap Pelaksanaan, Anda akan melakukan kegiatan sesuai
                  diskusi dan mengisi refleksi terhadap kegiatan yang sudah Anda
                  lakukan.
                </Text>
              </Text>
            </div>
          </div>

          <div className="flex flex-col gap-9 mt-9">
            <KinerjaDropdownGroupSingle
              content={contentPengembanganKompetensi}
              kinerja={skp?.pengembanganKompetensi ?? []}
              onChange={handleChange}
            />
          </div>
        </div>

        <Separator />

        <div className="px-8 py-4 flex gap-5 justify-end">
          {customStickyFooter ? (
            customStickyFooter()
          ) : (
            <>
              <Button
                size="lg"
                color="white"
                isLoading={isSubmitting}
                disabled={isSubmitting}
                onClick={onSaveDraft}
              >
                Simpan draf
              </Button>
              <Button
                size="lg"
                color="black"
                isLoading={isSubmitting}
                disabled={isSubmitting || isDisabledSubmitButton}
                type="submit"
              >
                Ke Perilaku Kerja
              </Button>
            </>
          )}
        </div>
      </form>
    </>
  )
}

export default forwardRef<SKPTabHandler, Props>(Tab2Year2025Onwards)
